import { defineStore } from "pinia";
import YearsApi from "../api/Api/YearsApi";
import { cloneDeep } from "lodash";
import ChampionshipTeamsApi from "../api/Api/ChampionshipTeamsApi";
import { DateTime, Interval } from "luxon";
import { useUserStore } from "./user";
import ChallengeFormsApi from "@/api/Api/ChallengeFormsApi";
import { useChallengesStore } from "@/store/challengeStore";

export const useAdminStore = defineStore("admin", {
  state: () => ({
    adminYear: {},
    adminYearBackup: {},
    dirtyTeams: [],
    loading: false,
  }),
  getters: {
    selectedSchoolListAvailable: (state) => {
      if (!state.adminYear || !state.adminYear.availDate) return false;
      const userStore = useUserStore();
      const publicStartDate = DateTime.fromISO(
        state.adminYear.availDate.publicStartDate
      );
      const publicEndDate = DateTime.fromISO(
        state.adminYear.availDate.publicEndDate
      );
      const committeeStartDate = DateTime.fromISO(
        state.adminYear.availDate.cmStartDate
      );
      const committeeEndDate = DateTime.fromISO(
        state.adminYear.availDate.cmEndDate
      );

      if (
        Interval.fromDateTimes(publicStartDate, publicEndDate).contains(
          DateTime.now()
        ) ||
        (Interval.fromDateTimes(committeeStartDate, committeeEndDate).contains(
          DateTime.now()
        ) &&
          userStore.isCommitteeMember)
      )
        return true;
      return false;
    },
    championshipTeamName: (state) => (id) => {
      return state.adminYear.championshipTeams.find((team) => team.id === id)
        .orgName;
    },
    teamAllowedToCreateChallenge: (state) => (id) => {
      // if we are displaying challenges for the team with the passed id , that team cannot create challenge.
      const userStore = useUserStore();
      // can't create challenge if you are not logged in , or are a staff.
      if (!userStore.isLoggedIn || userStore.isStaff) return false;
      const championshipTeam = state.adminYear.championshipTeams.find(
        (team) => team.id === id
      );
      if (!championshipTeam) return false;
      return championshipTeam.openChallengeFormsIds.length === 0;
    },
  },
  actions: {
    fetchAdminYear(yearId) {
      this.loading = true;
      const userStore = useUserStore();
      if (userStore.isCommitteeMember || userStore.isStaff) {
        useChallengesStore().fetchChallenges(yearId);
      }
      return YearsApi.get({ id: yearId })
        .then((response) => {
          this.adminYear = response;
          this.adminYearBackup = cloneDeep(response);
          return response;
        })
        .finally(() => {
          this.dirtyTeams.splice(0, this.dirtyTeams.length);
          this.loading = false;
        });
    },
    markTeamDirty(id) {
      // if team is not present mark it dirty
      if (!this.dirtyTeams.includes(id)) this.dirtyTeams.push(id);
      else {
        // when team was already present , check the state , if the state is same , remove it.
        const team = this.adminYear.championshipTeams.find(
          (team) => team.id === id
        );
        const backupTeam = this.adminYearBackup.championshipTeams.find(
          (team) => team.id === id
        );
        if (
          team.lateAccess === backupTeam.lateAccess &&
          team.championshipSelected === backupTeam.championshipSelected
        ) {
          const dirtyTeamIndex = this.dirtyTeams.findIndex(
            (teamId) => teamId === id
          );
          // delete that dirty team
          this.dirtyTeams.splice(dirtyTeamIndex, 1);
        }
      }
    },
    saveTeams() {
      const teams = this.adminYear.championshipTeams.filter((team) =>
        this.dirtyTeams.includes(team.id)
      );
      return ChampionshipTeamsApi.updateTeams({
        params: { yearId: this.adminYear.id },
        data: { teams },
      }).then(() => {
        this.adminYearBackup = cloneDeep(this.adminYear);
        // clear dirty teams list
        this.dirtyTeams.splice(0, this.dirtyTeams.length);
      });
    },
    updateSubmissionDate(teamToUpdate) {
      const team = this.adminYear.championshipTeams.find(
        (team) => team.id === teamToUpdate.id
      );
      if (team) {
        team.submissionDate = teamToUpdate.submissionDate;
        const backUpTeam = this.adminYearBackup.championshipTeams.find(
          (team) => team.id === teamToUpdate.id
        );
        backUpTeam.submissionDate = teamToUpdate.submissionDate;
      }
    },
    resetAdminYear() {
      this.adminYear = cloneDeep(this.adminYearBackup);
      this.dirtyTeams.splice(0, this.dirtyTeams.length);
    },
    getTeamIdsToPrint() {
      return this.adminYear.championshipTeams
        .filter((team) => team.print === true)
        .map((team) => team.id);
    },
  },
});
